import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useOperationLogs } from "./useOperationLogs";
import { useOperationalPeriod } from "./useOperationalPeriod";
import { useAutoClosePeriod } from "./useAutoClosePeriod";
import { printCashMovement, printCashRegisterClose } from "@/lib/cash-printing";
import { logDedupError } from "@/lib/error-dedup";
import { getIsOnline } from "@/lib/connectivity";

export type CashRegisterStatus = "open" | "closed";
export type CashMovementType = "withdrawal" | "supply";

export interface CashRegister {
  id: string;
  restaurant_id: string;
  opened_by: string;
  closed_by: string | null;
  opening_amount: number;
  closing_amount: number | null;
  expected_amount: number | null;
  difference: number | null;
  status: CashRegisterStatus;
  opened_at: string;
  closed_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  period_id: string | null;
}

export interface CashMovement {
  id: string;
  cash_register_id: string;
  type: CashMovementType;
  amount: number;
  reason: string | null;
  performed_by: string;
  created_at: string;
  expense_id?: string | null;
}

const FETCH_DEBOUNCE_MS = 2000;
const CASH_REGISTER_STORAGE_KEY = "cash_register_state";
const CASH_MOVEMENTS_STORAGE_KEY = "cash_movements_state";
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

function saveCashRegisterToStorage(register: CashRegister | null, movements: CashMovement[]) {
  try {
    if (register) {
      localStorage.setItem(CASH_REGISTER_STORAGE_KEY, JSON.stringify(register));
      localStorage.setItem(CASH_MOVEMENTS_STORAGE_KEY, JSON.stringify(movements));
    } else {
      localStorage.removeItem(CASH_REGISTER_STORAGE_KEY);
      localStorage.removeItem(CASH_MOVEMENTS_STORAGE_KEY);
    }
  } catch (e) { /* ignore quota errors */ }
}

function loadCashRegisterFromStorage(): { register: CashRegister | null; movements: CashMovement[] } {
  try {
    const raw = localStorage.getItem(CASH_REGISTER_STORAGE_KEY);
    const movRaw = localStorage.getItem(CASH_MOVEMENTS_STORAGE_KEY);
    const register: CashRegister | null = raw ? JSON.parse(raw) : null;
    const movements: CashMovement[] = movRaw ? JSON.parse(movRaw) : [];
    if (register?.opened_at) {
      const age = Date.now() - new Date(register.opened_at).getTime();
      if (age > CACHE_TTL_MS) {
        try {
          localStorage.removeItem(CASH_REGISTER_STORAGE_KEY);
          localStorage.removeItem(CASH_MOVEMENTS_STORAGE_KEY);
        } catch { /* ignore */ }
        return { register: null, movements: [] };
      }
    }
    return { register, movements };
  } catch {
    return { register: null, movements: [] };
  }
}

export function useCashRegister() {
  const { activeRestaurant } = useRestaurantContext();
  const { user } = useAuth();
  const [currentRegister, setCurrentRegister] = useState<CashRegister | null>(null);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { logOperation } = useOperationLogs();
  const { currentPeriod, isPeriodOpen, openPeriod, refetch: refetchPeriod } = useOperationalPeriod();
  const { checkAndAutoClose, checkRequireManualClose, isProcessing: isAutoCloseProcessing } = useAutoClosePeriod();
  const hasCheckedAutoClose = useRef(false);
  const lastFetchTime = useRef<number>(0);
  const isFetching = useRef(false);

  const fetchCurrentRegister = useCallback(async () => {
    if (!activeRestaurant?.id) {
      setIsLoading(false);
      return;
    }

    // Debounce: prevent cascading requests
    const now = Date.now();
    if (now - lastFetchTime.current < FETCH_DEBOUNCE_MS) {
      return;
    }

    // Prevent concurrent fetches
    if (isFetching.current) {
      return;
    }

    // Skip network if offline (heartbeat) — restore from cache
    if (!getIsOnline()) {
      const cached = loadCashRegisterFromStorage();
      if (cached.register && cached.register.restaurant_id === activeRestaurant.id) {
        setCurrentRegister(cached.register);
        setMovements(cached.movements);
        console.log('[CashRegister] Restored from localStorage (offline)');
      }
      setIsLoading(false);
      return;
    }

    lastFetchTime.current = now;
    isFetching.current = true;

    try {
      const { data, error } = await supabase
        .from("cash_registers")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .eq("status", "open")
        .order("opened_at", { ascending: false })
        .maybeSingle();

      if (error) throw error;

      setCurrentRegister(data as CashRegister | null);

      if (data) {
        const mov = await fetchMovementsRaw(data.id);
        setMovements(mov);
        // Persist to localStorage for offline resilience (use fresh movements, not stale closure)
        saveCashRegisterToStorage(data as CashRegister, mov);
      } else {
        saveCashRegisterToStorage(null, []);
      }
    } catch (error) {
      logDedupError("CashRegister.fetch", error);
      // Fallback: hydrate from cache so PDV doesn't block on lie-fi/abort
      if (!currentRegister) {
        const cached = loadCashRegisterFromStorage();
        if (cached.register && cached.register.restaurant_id === activeRestaurant.id) {
          setCurrentRegister(cached.register);
          setMovements(cached.movements);
          console.log('[CashRegister] Restored from localStorage (fetch failed)');
        }
      }
    } finally {
      setIsLoading(false);
      isFetching.current = false;
    }
  }, [activeRestaurant?.id]);

  const fetchMovementsRaw = async (registerId: string): Promise<CashMovement[]> => {
    const { data, error } = await supabase
      .from("cash_movements")
      .select("*")
      .eq("cash_register_id", registerId)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching movements:", error);
      return [];
    }
    return (data || []) as CashMovement[];
  };

  const fetchMovements = async (registerId: string) => {
    const mov = await fetchMovementsRaw(registerId);
    setMovements(mov);
  };

  const openRegister = async (openingAmount: number) => {
    if (!activeRestaurant || !user) {
      toast.error("Usuário ou restaurante não identificado");
      return null;
    }

    try {
      // Auto-open period if not open
      let periodId = currentPeriod?.id;
      if (!isPeriodOpen) {
        const newPeriod = await openPeriod();
        if (!newPeriod) {
          toast.error("Não foi possível abrir o período");
          return null;
        }
        periodId = newPeriod.id;
        await refetchPeriod();
      }

      const { data, error } = await supabase
        .from("cash_registers")
        .insert({
          restaurant_id: activeRestaurant.id,
          opened_by: user.id,
          opening_amount: openingAmount,
          status: "open",
          period_id: periodId,
        })
        .select()
        .single();

      if (error) throw error;

      await logOperation("open", "cash_register", data.id, {
        opening_amount: openingAmount,
        period_id: periodId,
      });

      setCurrentRegister(data as CashRegister);
      setMovements([]);
      saveCashRegisterToStorage(data as CashRegister, []);
      toast.success("Caixa aberto com sucesso!");
      return data;
    } catch (error) {
      console.error("Error opening register:", error);
      toast.error("Erro ao abrir caixa");
      return null;
    }
  };

  const closeRegister = async (closingAmount: number, notes?: string) => {
    if (!currentRegister || !user || !activeRestaurant) {
      toast.error("Nenhum caixa aberto");
      return null;
    }

    try {
      // Calculate expected amount
      const supplies = movements
        .filter((m) => m.type === "supply")
        .reduce((sum, m) => sum + Number(m.amount), 0);
      const withdrawals = movements
        .filter((m) => m.type === "withdrawal")
        .reduce((sum, m) => sum + Number(m.amount), 0);

      // Get cash sales for this register period
      const { data: cashSales } = await supabase
        .from("order_payments")
        .select(`
          amount,
          order:orders!inner(
            restaurant_id,
            created_at
          ),
          payment_method:payment_methods!inner(type)
        `)
        .eq("payment_method.type", "cash")
        .gte("created_at", currentRegister.opened_at);

      const cashFromSales = cashSales
        ?.filter((sale: any) => sale.order?.restaurant_id === activeRestaurant?.id)
        .reduce((sum: number, sale: any) => sum + Number(sale.amount), 0) || 0;

      const expectedAmount =
        Number(currentRegister.opening_amount) +
        supplies -
        withdrawals +
        cashFromSales;

      const difference = closingAmount - expectedAmount;
      const closedAt = new Date().toISOString();

      const { data, error } = await supabase
        .from("cash_registers")
        .update({
          closed_by: user.id,
          closing_amount: closingAmount,
          expected_amount: expectedAmount,
          difference,
          status: "closed",
          closed_at: closedAt,
          notes,
        })
        .eq("id", currentRegister.id)
        .select()
        .single();

      if (error) throw error;

      await logOperation("close", "cash_register", currentRegister.id, {
        opening_amount: currentRegister.opening_amount,
        closing_amount: closingAmount,
        expected_amount: expectedAmount,
        difference,
        cash_sales: cashFromSales,
        supplies,
        withdrawals,
      });

      // Print close receipt automatically
      printCashRegisterClose({
        cashRegisterId: currentRegister.id,
        restaurantId: activeRestaurant.id,
        openedAt: currentRegister.opened_at,
        closedAt,
        openingAmount: Number(currentRegister.opening_amount),
        supplies,
        withdrawals,
        cashSales: cashFromSales,
        expectedAmount,
        countedAmount: closingAmount,
        difference,
        notes,
      });

      setCurrentRegister(null);
      setMovements([]);
      saveCashRegisterToStorage(null, []);
      toast.success("Caixa fechado com sucesso!");
      return data;
    } catch (error) {
      console.error("Error closing register:", error);
      toast.error("Erro ao fechar caixa");
      return null;
    }
  };

  const addMovement = async (
    type: CashMovementType,
    amount: number,
    reason?: string,
    expense?: {
      title: string;
      category_id: string;
      supplier_id?: string;
      due_date?: string;
    }
  ) => {
    if (!currentRegister || !user || !activeRestaurant) {
      toast.error("Nenhum caixa aberto");
      return null;
    }

    try {
      let expenseId: string | null = null;

      if (type === "withdrawal" && expense) {
        const today = new Date().toISOString().slice(0, 10);
        const { data: createdExpense, error: expenseError } = await supabase
          .from("expenses" as any)
          .insert({
            restaurant_id: activeRestaurant.id,
            title: expense.title,
            description: reason || null,
            amount,
            status: "open",
            expense_date: today,
            due_date: expense.due_date || null,
            category_id: expense.category_id,
            supplier_id: expense.supplier_id || null,
            origin: "cash_withdrawal",
            created_by: user.id,
          })
          .select("id")
          .single();

        if (expenseError) throw expenseError;
        expenseId = (createdExpense as unknown as { id?: string } | null)?.id || null;
      }

      const { data, error } = await supabase
        .from("cash_movements")
        .insert({
          cash_register_id: currentRegister.id,
          type,
          amount,
          reason,
          performed_by: user.id,
          expense_id: expenseId,
        })
        .select()
        .single();

      if (error) throw error;

      await logOperation("create", "cash_movement", data.id, {
        type,
        amount,
        reason,
        expense_id: expenseId,
      });

      // back-link for easier navigation (optional)
      if (expenseId) {
        await supabase
          .from("expenses" as any)
          .update({ linked_cash_movement_id: data.id })
          .eq("id", expenseId);
      }

      setMovements((prev) => [data as CashMovement, ...prev]);
      toast.success(
        type === "withdrawal" ? "Sangria registrada!" : "Suprimento registrado!"
      );

      // Print movement receipt automatically
      printCashMovement({
        type,
        amount,
        reason,
        restaurantId: activeRestaurant.id,
      });

      return data;
    } catch (error) {
      console.error("Error adding movement:", error);
      toast.error("Erro ao registrar movimentação");
      return null;
    }
  };

  const getTotals = useCallback(() => {
    const supplies = movements
      .filter((m) => m.type === "supply")
      .reduce((sum, m) => sum + Number(m.amount), 0);
    const withdrawals = movements
      .filter((m) => m.type === "withdrawal")
      .reduce((sum, m) => sum + Number(m.amount), 0);
    
    return {
      supplies,
      withdrawals,
      openingAmount: Number(currentRegister?.opening_amount || 0),
    };
  }, [movements, currentRegister]);

  // Check for auto-close on initial load - stable dependencies to prevent loops
  useEffect(() => {
    if (!activeRestaurant?.id) return;
    if (hasCheckedAutoClose.current) return;

    const initializeAndCheck = async () => {
      hasCheckedAutoClose.current = true;

      // Skip all network operations if offline — restore from localStorage
      if (!getIsOnline()) {
        const cached = loadCashRegisterFromStorage();
        if (cached.register && cached.register.restaurant_id === activeRestaurant.id) {
          setCurrentRegister(cached.register);
          setMovements(cached.movements);
          console.log('[CashRegister] Restored from localStorage (offline)');
        }
        setIsLoading(false);
        return;
      }

      // First, check if auto-close is needed
      const result = await checkAndAutoClose();
      
      // If auto-closed, refetch period to get the new one
      if (result.wasAutoClosed) {
        await refetchPeriod();
      } else if (result.requiresManualIntervention) {
        // Auto-close couldn't proceed due to open registers — force manual close check
        console.log("[CashRegister] Auto-close blocked by open registers, forcing manual close check");
        const manualResult = await checkRequireManualClose(true);
        if (manualResult.shouldBlock) {
          console.log("[CashRegister] Manual close required — PDV will be blocked");
        }
      }

      // Then fetch the current register
      await fetchCurrentRegister();
    };

    initializeAndCheck();
  }, [activeRestaurant?.id]); // Stable dependency - only restaurant ID

  return {
    currentRegister,
    movements,
    isLoading,
    isOpen: !!currentRegister,
    currentPeriod,
    isPeriodOpen,
    openRegister,
    closeRegister,
    addMovement,
    getTotals,
    refetch: fetchCurrentRegister,
    checkRequireManualClose,
  };
}
